import { useMemo } from "react";
import {
  DndContext,
  type DragEndEvent,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import { Lane } from "./Lane";
import { ListingDetail } from "./ListingDetail";
import { useUI } from "@/store/ui";
import { useUpdateListing } from "@/hooks/useListings";
import { STATUSES, type Listing, type Status } from "@/types";

interface BoardProps {
  listings: Listing[];
  hideRejected: boolean;
}

export function Board({ listings, hideRejected }: BoardProps) {
  const update = useUpdateListing();
  const selectedId = useUI((s) => s.selectedId);
  const visibleStatuses = useMemo<Status[]>(
    () => (hideRejected ? STATUSES.filter((s) => s !== "Rejected") : STATUSES),
    [hideRejected],
  );
  // Only require ~5px movement to activate drag, so click-to-open still works.
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
  );

  const grouped = useMemo(() => {
    const m: Record<Status, Listing[]> = {
      Inbox: [], Interesting: [], Contacted: [],
      Viewing: [], Applied: [], Rejected: [],
    };
    for (const l of listings) m[l.status]?.push(l);
    return m;
  }, [listings]);

  function onDragEnd(e: DragEndEvent) {
    const id = e.active.id as string;
    const overId = e.over?.id as string | undefined;
    if (!overId) return;
    // If dropped on a card, look up that card's lane; otherwise overId is the lane.
    const target = listings.find((l) => l.id === overId);
    const newStatus = (target ? target.status : (overId as Status)) as Status;
    const current = listings.find((l) => l.id === id);
    if (!current || current.status === newStatus) return;
    update.mutate({ id, patch: { status: newStatus } });
  }

  const selected = selectedId
    ? listings.find((l) => l.id === selectedId) ?? null
    : null;

  return (
    <>
      <DndContext sensors={sensors} onDragEnd={onDragEnd}>
        <div className="flex h-full gap-3 overflow-x-auto px-4 py-4">
          {visibleStatuses.map((status) => (
            <Lane
              key={status}
              status={status}
              listings={grouped[status] ?? []}
            />
          ))}
        </div>
      </DndContext>
      {selected && <ListingDetail listing={selected} />}
    </>
  );
}
